import React from "react";
import {
  Box,
  Button,
  Chip,
  Container,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";
import AutoAwesomeRoundedIcon from "@mui/icons-material/AutoAwesomeRounded";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import Groups2RoundedIcon from "@mui/icons-material/Groups2Rounded";
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";
import LanguageRoundedIcon from "@mui/icons-material/LanguageRounded";
import PaymentsRoundedIcon from "@mui/icons-material/PaymentsRounded";
import PointOfSaleRoundedIcon from "@mui/icons-material/PointOfSaleRounded";
import EventAvailableRoundedIcon from "@mui/icons-material/EventAvailableRounded";
import { formatAcquisitionMetric } from "../../utils/acquisitionFunnel";

// BLOCK 2 · Homepage becomes an interactive product menu instead of a long explanation.
// STATUS: STATIC_PASS. Runtime/responsive verification pending staging deploy.
const BRAND = { orange: "#F35A3A", orangeSoft: "#FFB19A", ink: "#F8FAFC", panel: "#111820" };

const QUICK_NEEDS = [
  { key: "sell", label: "Vender", icon: PointOfSaleRoundedIcon },
  { key: "stock", label: "Stock", icon: Inventory2OutlinedIcon },
  { key: "cash", label: "Caja", icon: PaymentsRoundedIcon },
  { key: "customers", label: "Clientes", icon: Groups2RoundedIcon },
  { key: "appointments", label: "Turnos", icon: EventAvailableRoundedIcon },
  { key: "website", label: "Mi web", icon: LanguageRoundedIcon },
];

function RealStats({ stats = [] }) {
  if (!stats.length) return null;
  return (
    <Box sx={{ mt: { xs: 2.5, sm: 3.2 }, display: "grid", gridTemplateColumns: { xs: "repeat(3, minmax(0, 1fr))" }, gap: { xs: .7, sm: 1 } }}>
      {stats.slice(0, 3).map((item) => (
        <Box key={item.key} sx={{ px: { xs: .8, sm: 1.2 }, py: { xs: 1, sm: 1.25 }, borderRadius: 2.8, border: "1px solid rgba(255,255,255,.07)", bgcolor: "rgba(255,255,255,.035)", minWidth: 0 }}>
          <Typography sx={{ fontWeight: 950, fontSize: { xs: "1rem", sm: "1.28rem" }, lineHeight: 1.05, color: "#fff" }}>
            {formatAcquisitionMetric(item.value)}
          </Typography>
          <Typography sx={{ mt: .25, color: "rgba(255,255,255,.48)", fontWeight: 750, fontSize: { xs: ".64rem", sm: ".73rem" }, lineHeight: 1.2 }}>
            {item.label}
          </Typography>
        </Box>
      ))}
    </Box>
  );
}

export default function HeroSection({
  onStartClick,
  onExploreClick,
  onQuickNeed,
  stats = [],
  primaryLabel = "Empezar",
  isAuthenticated = false,
  userName = "",
}) {
  return (
    <Box
      component="section"
      aria-labelledby="webprofit-home-title"
      sx={{
        position: "relative",
        overflow: "hidden",
        minHeight: { xs: "auto", md: "min(900px, 100vh)" },
        display: "flex",
        alignItems: "center",
        pt: { xs: 11, sm: 13, md: 14 },
        pb: { xs: 6, sm: 8, md: 9 },
        color: BRAND.ink,
        bgcolor: "#0B1016",
        background: "radial-gradient(circle at 82% 18%, rgba(243,90,58,.19), transparent 28%), radial-gradient(circle at 12% 78%, rgba(255,126,87,.08), transparent 30%), linear-gradient(180deg, #090E13 0%, #0B1118 100%)",
        "@keyframes homeGlowFloat": {
          "0%, 100%": { transform: "translate3d(0,0,0) scale(1)" },
          "50%": { transform: "translate3d(-26px,20px,0) scale(1.06)" },
        },
      }}
    >
      <Box aria-hidden sx={{ position: "absolute", width: { xs: 360, md: 620 }, height: { xs: 360, md: 620 }, right: { xs: -230, md: -180 }, top: { xs: 40, md: -30 }, borderRadius: "50%", background: "radial-gradient(circle, rgba(243,90,58,.30), rgba(243,90,58,0) 68%)", filter: "blur(12px)", animation: "homeGlowFloat 9s ease-in-out infinite", pointerEvents: "none", "@media (prefers-reduced-motion: reduce)": { animation: "none" } }} />
      <Box aria-hidden sx={{ position: "absolute", inset: 0, opacity: .2, backgroundImage: "linear-gradient(rgba(255,255,255,.045) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,.045) 1px, transparent 1px)", backgroundSize: "46px 46px", maskImage: "linear-gradient(to bottom, rgba(0,0,0,.78), transparent 92%)", pointerEvents: "none" }} />

      <Container maxWidth="lg" sx={{ position: "relative", zIndex: 1 }}>
        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "minmax(0, 1fr) minmax(380px, .82fr)" }, gap: { xs: 4, md: 7.5 }, alignItems: "center" }}>
          <Box sx={{ maxWidth: 700 }}>
            <Chip
              icon={<CheckCircleRoundedIcon />}
              label={isAuthenticated ? `Sesión activa${userName ? ` · ${userName}` : ""}` : "WebProfit · tu negocio en un solo lugar"}
              sx={{ mb: { xs: 1.8, sm: 2.3 }, minHeight: 34, borderRadius: 999, bgcolor: "rgba(243,90,58,.11)", color: BRAND.orangeSoft, border: "1px solid rgba(243,90,58,.16)", fontWeight: 900, "& .MuiChip-icon": { color: BRAND.orange } }}
            />

            <Typography id="webprofit-home-title" component="h1" sx={{ color: "#F8FAFC", fontWeight: 950, letterSpacing: "-.058em", lineHeight: .98, fontSize: { xs: "2.65rem", sm: "3.8rem", md: "4.65rem", lg: "5.1rem" } }}>
              Tu negocio,
              <Box component="span" sx={{ display: "block", color: BRAND.orange }}>más claro.</Box>
            </Typography>

            <Typography sx={{ mt: { xs: 1.6, sm: 2.1 }, maxWidth: 580, color: "rgba(247,248,250,.62)", fontSize: { xs: ".96rem", sm: "1.06rem" }, lineHeight: 1.55, fontWeight: 600 }}>
              Web, ventas y gestión conectadas. Empezá por lo que hoy más te frena.
            </Typography>

            <Stack direction={{ xs: "column", sm: "row" }} spacing={1.1} sx={{ mt: { xs: 2.6, sm: 3.2 }, maxWidth: 540 }}>
              <Button onClick={onStartClick} variant="contained" size="large" endIcon={<ArrowForwardRoundedIcon />} sx={{ minHeight: 54, px: 3, borderRadius: 999, bgcolor: BRAND.orange, textTransform: "none", fontWeight: 950, boxShadow: "0 16px 38px rgba(243,90,58,.30)", "&:hover": { bgcolor: "#E64A2A", transform: "translateY(-1px)" } }}>
                {primaryLabel}
              </Button>
              <Button onClick={onExploreClick} variant="outlined" size="large" startIcon={<AutoAwesomeRoundedIcon />} sx={{ minHeight: 54, px: 2.6, borderRadius: 999, color: "#F8FAFC", borderColor: "rgba(255,255,255,.19)", bgcolor: "rgba(255,255,255,.025)", textTransform: "none", fontWeight: 850, "&:hover": { borderColor: "rgba(255,255,255,.42)", bgcolor: "rgba(255,255,255,.055)" } }}>
                ¿Qué es WebProfit?
              </Button>
            </Stack>

            <RealStats stats={stats} />
          </Box>

          <Paper elevation={0} sx={{ position: "relative", overflow: "hidden", p: { xs: 1.5, sm: 2 }, borderRadius: { xs: 4, sm: 5 }, bgcolor: "rgba(17,24,32,.88)", color: "#fff", border: "1px solid rgba(255,255,255,.09)", boxShadow: "0 34px 90px rgba(0,0,0,.38)", backdropFilter: "blur(20px)" }}>
            <Box sx={{ px: { xs: .4, sm: .8 }, pt: .5, pb: 1.5 }}>
              <Typography sx={{ color: BRAND.orangeSoft, fontWeight: 950, letterSpacing: ".09em", textTransform: "uppercase", fontSize: ".7rem" }}>Entrada rápida</Typography>
              <Typography sx={{ mt: .55, fontWeight: 950, letterSpacing: "-.03em", fontSize: { xs: "1.4rem", sm: "1.75rem" } }}>¿Qué querés ordenar primero?</Typography>
            </Box>

            <Box sx={{ display: "grid", gridTemplateColumns: "repeat(2, minmax(0,1fr))", gap: .9 }}>
              {QUICK_NEEDS.map(({ key, label, icon: Icon }) => (
                <Button
                  key={key}
                  onClick={() => onQuickNeed?.(key)}
                  variant="outlined"
                  sx={{
                    justifyContent: "flex-start",
                    minHeight: { xs: 62, sm: 70 },
                    px: { xs: 1.3, sm: 1.55 },
                    borderRadius: 3,
                    textTransform: "none",
                    fontWeight: 900,
                    fontSize: { xs: ".86rem", sm: ".94rem" },
                    color: "rgba(255,255,255,.86)",
                    borderColor: "rgba(255,255,255,.08)",
                    bgcolor: "rgba(255,255,255,.035)",
                    gap: 1,
                    "&:hover": { bgcolor: "rgba(243,90,58,.11)", borderColor: "rgba(243,90,58,.30)", color: "#fff", transform: "translateY(-1px)" },
                  }}
                >
                  <Box sx={{ width: 34, height: 34, borderRadius: 2.2, display: "grid", placeItems: "center", bgcolor: "rgba(243,90,58,.10)", color: BRAND.orangeSoft, flexShrink: 0 }}><Icon sx={{ fontSize: 19 }} /></Box>
                  {label}
                </Button>
              ))}
            </Box>

            <Typography sx={{ mt: 1.45, px: .8, color: "rgba(255,255,255,.42)", fontSize: ".73rem", fontWeight: 700 }}>
              Elegís una prioridad. WebProfit arma el camino inicial alrededor de tu negocio.
            </Typography>
          </Paper>
        </Box>
      </Container>
    </Box>
  );
}
